/**
 * <Card> — elevated container primitive with header/body/footer slots.
 *
 * API matches D-05 hybrid pattern: composed dotted API (`<Card.Header>`,
 * `<Card.Body>`, `<Card.Footer>`) AND a single-root wrapper for the 80% case.
 *
 * UI-SPEC defaults:
 *   - Radius: `rounded-lg` (8px, D-03 card tier).
 *   - Background: `bg-surface-elevated` (raised one level above app surface).
 *   - Border: `border border-subtle`.
 *   - Shadow: none — the 2026-09 theme keeps cards flat (hairline border only);
 *     elevation is reserved for popovers/dialogs.
 *   - Padding: `p-lg` (24px) by default, collapses to `p-md` with `density="compact"`.
 */
import * as React from "react";
import { cn, cva, type VariantProps } from "../../utils";

export const cardVariants = cva(
  cn(
    // `linkhr-glass-soft` is a no-op when `[data-glass="on"]` is not set;
    // when active it overrides bg / border / shadow with the body-content
    // glass variant (translucent + warm-primary wash + no blur for perf).
    "linkhr-glass-soft bg-surface-elevated border border-subtle rounded-lg",
    "flex flex-col",
  ),
  {
    variants: {
      density: {
        comfortable: "p-lg gap-md",
        compact: "p-md gap-sm",
      },
    },
    defaultVariants: { density: "comfortable" },
  },
);

export type CardProps = React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof cardVariants>;

const CardRoot = React.forwardRef<HTMLDivElement, CardProps>(function Card(
  { className, density, ...rest },
  ref,
) {
  return <div ref={ref} className={cn(cardVariants({ density }), className)} {...rest} />;
});

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function CardHeader({ className, ...rest }, ref) {
    return <div ref={ref} className={cn("flex flex-col gap-xs", className)} {...rest} />;
  },
);

const CardTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  function CardTitle({ className, children, ...rest }, ref) {
    return (
      <h3 ref={ref} className={cn("text-heading", className)} {...rest}>
        {children}
      </h3>
    );
  },
);

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(function CardDescription({ className, ...rest }, ref) {
  return <p ref={ref} className={cn("text-label", className)} {...rest} />;
});

const CardBody = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function CardBody({ className, ...rest }, ref) {
    return <div ref={ref} className={cn("flex flex-col gap-sm text-body", className)} {...rest} />;
  },
);

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function CardFooter({ className, ...rest }, ref) {
    return (
      <div
        ref={ref}
        className={cn(
          "flex items-center justify-end gap-sm pt-md border-t border-subtle",
          className,
        )}
        {...rest}
      />
    );
  },
);

// Composed dotted API per D-05.
type CardComposition = typeof CardRoot & {
  Header: typeof CardHeader;
  Title: typeof CardTitle;
  Description: typeof CardDescription;
  Body: typeof CardBody;
  Footer: typeof CardFooter;
};

export const Card = CardRoot as CardComposition;
Card.Header = CardHeader;
Card.Title = CardTitle;
Card.Description = CardDescription;
Card.Body = CardBody;
Card.Footer = CardFooter;
